import {
  MAX_VOLUMES_PER_SERVER,
  VOLUME_MAX_GB,
  VOLUME_MIN_GB,
  volumeMonthly,
  type PriceList,
  type Region,
  type Server,
  type Volume,
  type VolumeFormat,
} from "@cloudserver/contracts";
import { useReadOnly } from "../api/users.js";
import { ChangeCost } from "./costs/change-cost.js";
import {
  Expand,
  HardDrive,
  Link2,
  Lock,
  LockOpen,
  Pencil,
  Plus,
  Trash2,
  Unlink,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../api/client.js";
import { progressOf, useRunningOperations } from "../api/operations.js";
import { useCatalogue } from "../api/queries.js";
import { useServers } from "../api/servers.js";
import {
  useAttachVolume,
  useCreateVolume,
  useDeleteVolume,
  useDetachVolume,
  useResizeVolume,
  useUpdateVolume,
  useVolumeProtection,
  useVolumes,
} from "../api/volumes.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError, problemText } from "../ui/feedback.js";
import { money, relativeTime } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Dialog, type MenuItem } from "../ui/overlay.js";
import { Alert, Badge, Button, TextArea, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { formatLabels, parseLabels } from "./labels.js";

/*
 * Volumes: the list page, and what a server's volumes tab shares with it —
 * the row menu and its dialogs (create, attach, detach, grow, name and
 * labels, delete), prices and the reasons a server cannot take a volume.
 */

type T = ReturnType<typeof useTranslation>["t"];

const selectClass = "min-h-10 rounded-lg border border-line bg-surface px-3 text-sm";

/** Monthly price of `sizeGb`, formatted; a dash until the price list has loaded. */
export function volumePrice(sizeGb: number, prices: PriceList | undefined, locale: string): string {
  if (!prices) return "—";
  return money(String(volumeMonthly(sizeGb, prices.volumePerGbMonth.net)), prices.currency, locale);
}

/** "250 GB · ext4": the size and the file system the provider made. */
export function volumeLine(volume: Volume, t: T): string {
  return `${t("volumes.sizeGb", { size: volume.sizeGb })} · ${volume.format ?? t("volumes.noFs")}`;
}

/** Why a server cannot take this volume now, or null when it can. */
function serverBlocker(t: T, server: Server, region: string | null, volumes: Volume[]) {
  if (region !== null && server.region !== region) return t("volumes.why.otherRegion");
  const count = volumes.filter((v) => v.server?.id === server.id).length;
  if (count >= MAX_VOLUMES_PER_SERVER) return t("volumes.why.full", { count });
  return null;
}

function ServerSelect({
  servers,
  volumes,
  region,
  value,
  onChange,
}: {
  servers: Server[];
  volumes: Volume[];
  /** Only servers of this location can take the volume; null for a new one. */
  region: string | null;
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium">{t("volumes.server")}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={selectClass}
        required
      >
        <option value="">{t("volumes.chooseServer")}</option>
        {servers.map((s) => {
          const why = serverBlocker(t, s, region, volumes);
          return (
            <option key={s.id} value={s.id} disabled={why !== null}>
              {s.name} ({s.region}){why ? ` — ${why}` : ""}
            </option>
          );
        })}
      </select>
      <span className="text-xs text-muted">
        {t("volumes.serverHint", { max: MAX_VOLUMES_PER_SERVER })}
      </span>
    </label>
  );
}

/** Locations grouped by network zone, for a new volume (or, with its own texts, an address). */
export function RegionSelect({
  regions,
  value,
  onChange,
  label,
  hint,
}: {
  regions: Region[];
  value: string;
  onChange: (code: string) => void;
  label?: string;
  hint?: string;
}) {
  const { t } = useTranslation();
  const zones = [...new Set(regions.map((r) => r.zone))];
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium">{label ?? t("volumes.location")}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
        {zones.map((zone) => (
          <optgroup key={zone} label={t("volumes.zone", { zone })}>
            {regions
              .filter((r) => r.zone === zone)
              .map((r) => (
                <option key={r.code} value={r.code}>
                  {r.city} ({r.code})
                </option>
              ))}
          </optgroup>
        ))}
      </select>
      <span className="text-xs text-muted">{hint ?? t("volumes.locationHint")}</span>
    </label>
  );
}

/** The file system the provider makes on a new volume, or none. */
export function FormatChoice({
  format,
  onChange,
}: {
  format: VolumeFormat | null;
  onChange: (format: VolumeFormat | null) => void;
}) {
  const { t } = useTranslation();
  const options: { value: VolumeFormat | null; label: string; hint: string }[] = [
    { value: "ext4", label: "ext4", hint: t("volumes.formatExt4Hint") },
    { value: "xfs", label: "XFS", hint: t("volumes.formatXfsHint") },
    { value: null, label: t("volumes.formatNone"), hint: t("volumes.formatNoneHint") },
  ];
  return (
    <fieldset className="flex flex-col gap-2 text-sm">
      <legend className="mb-1 font-medium">{t("volumes.format")}</legend>
      {options.map((o) => (
        <label
          key={o.label}
          className="flex cursor-pointer items-start gap-2 rounded-lg border border-line px-3 py-2 hover:bg-surface-2"
        >
          <input
            type="radio"
            name="format"
            checked={format === o.value}
            onChange={() => onChange(o.value)}
            className="mt-0.5 size-4 accent-accent"
          />
          <span className="flex flex-col">
            <span className="font-medium">{o.label}</span>
            <span className="text-muted">{o.hint}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/** The price of a size, with how it is computed. */
export function PriceLine({ sizeGb, projectId }: { sizeGb: number; projectId: string }) {
  const { t, i18n } = useTranslation();
  const prices = useCatalogue(projectId, "prices").data;
  if (!prices || !Number.isFinite(sizeGb) || sizeGb <= 0) return null;
  return (
    <p className="text-sm">
      {t("volumes.priceLine", {
        price: volumePrice(sizeGb, prices, i18n.language),
        size: sizeGb,
        perGb: money(prices.volumePerGbMonth.net, prices.currency, i18n.language, 4),
      })}
    </p>
  );
}

function CreateDialog({
  projectId,
  servers,
  volumes,
  serverId: presetServer,
  onClose,
}: {
  projectId: string;
  servers: Server[];
  volumes: Volume[];
  /** Opened from a server: the volume goes on it. */
  serverId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const regions = useCatalogue(projectId, "regions").data ?? [];
  const create = useCreateVolume(projectId);
  const toast = useToast();
  const [name, setName] = useState("");
  const [size, setSize] = useState(String(VOLUME_MIN_GB));
  const [target, setTarget] = useState<"region" | "server">(presetServer ? "server" : "region");
  const [region, setRegion] = useState("");
  const [serverId, setServerId] = useState(presetServer ?? "");
  const [format, setFormat] = useState<VolumeFormat | null>("ext4");
  const [automount, setAutomount] = useState(true);
  const sizeGb = Number(size);
  const chosenRegion = region || regions[0]?.code || "";
  const onServer = target === "server";

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        name: name.trim(),
        sizeGb,
        format,
        ...(onServer
          ? { serverId, automount: automount && format !== null }
          : { region: chosenRegion }),
      },
      {
        onSuccess: ({ volume }) => {
          toast({ tone: "ok", title: t("volumes.created", { name: volume.name }) });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("volumes.create")}
      description={t("volumes.createLead")}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="create-volume"
            busy={create.isPending}
            disabled={onServer && !serverId}
          >
            {t("volumes.createSubmit")}
          </Button>
        </>
      }
    >
      <form id="create-volume" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("volumes.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={64}
          spellCheck={false}
          autoCapitalize="off"
          hint={t("volumes.nameHint")}
          error={fieldError(create.error, "name", t)}
        />
        <TextField
          label={t("volumes.size")}
          type="number"
          inputMode="numeric"
          min={VOLUME_MIN_GB}
          max={VOLUME_MAX_GB}
          step={1}
          value={size}
          onChange={(e) => setSize(e.target.value)}
          required
          hint={t("volumes.sizeHint", { min: VOLUME_MIN_GB, max: VOLUME_MAX_GB })}
          error={fieldError(create.error, "sizeGb", t)}
        />
        <PriceLine sizeGb={sizeGb} projectId={projectId} />
        <ChangeCost
          projectId={projectId}
          change={
            Number.isInteger(sizeGb) && sizeGb >= VOLUME_MIN_GB && sizeGb <= VOLUME_MAX_GB
              ? onServer
                ? serverId
                  ? { action: "volume.create", sizeGb, serverId }
                  : null
                : chosenRegion
                  ? { action: "volume.create", sizeGb, region: chosenRegion }
                  : null
              : null
          }
        />
        {!presetServer && (
          <fieldset className="flex flex-col gap-2 text-sm">
            <legend className="mb-1 font-medium">{t("volumes.where")}</legend>
            <label className="flex min-h-6 items-center gap-2">
              <input
                type="radio"
                name="target"
                checked={!onServer}
                onChange={() => setTarget("region")}
                className="size-4 accent-accent"
              />
              {t("volumes.keepFree")}
            </label>
            <label className="flex min-h-6 items-center gap-2">
              <input
                type="radio"
                name="target"
                checked={onServer}
                onChange={() => setTarget("server")}
                className="size-4 accent-accent"
              />
              {t("volumes.onServer")}
            </label>
          </fieldset>
        )}
        {onServer ? (
          !presetServer && (
            <ServerSelect
              servers={servers}
              volumes={volumes}
              region={null}
              value={serverId}
              onChange={setServerId}
            />
          )
        ) : (
          <RegionSelect regions={regions} value={chosenRegion} onChange={setRegion} />
        )}
        <FormatChoice format={format} onChange={setFormat} />
        {onServer && (
          <label className="flex min-h-6 items-start gap-2 py-0.5 text-sm">
            <input
              type="checkbox"
              checked={automount && format !== null}
              disabled={format === null}
              onChange={(e) => setAutomount(e.target.checked)}
              className="mt-0.5 size-4 accent-accent"
            />
            <span>
              {t("volumes.automount")}
              <span className="block text-xs text-muted">{t("volumes.automountHint")}</span>
            </span>
          </label>
        )}
        <Alert tone="neutral">{t("volumes.billing")}</Alert>
        <ErrorMessage error={create.error} />
      </form>
    </Dialog>
  );
}

function AttachDialog({
  projectId,
  volume,
  servers,
  volumes,
  serverId: presetServer,
  onClose,
}: {
  projectId: string;
  volume: Volume;
  servers: Server[];
  volumes: Volume[];
  /** Opened from a server: chosen already. */
  serverId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const attach = useAttachVolume(projectId);
  const toast = useToast();
  const [serverId, setServerId] = useState(presetServer ?? "");
  const [automount, setAutomount] = useState(volume.format !== null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    attach.mutate(
      { id: volume.id, body: { serverId, automount: automount && volume.format !== null } },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("volumes.attaching", { name: volume.name }) });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("volumes.attachTitle", { name: volume.name })}
      description={t("volumes.attachLead", { region: volume.region, device: volume.linuxDevice })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="attach-volume" busy={attach.isPending} disabled={!serverId}>
            {t("volumes.attach")}
          </Button>
        </>
      }
    >
      <form id="attach-volume" onSubmit={submit} className="flex flex-col gap-4">
        <ServerSelect
          servers={servers}
          volumes={volumes}
          region={volume.region}
          value={serverId}
          onChange={setServerId}
        />
        {volume.format !== null && (
          <label className="flex min-h-6 items-start gap-2 py-0.5 text-sm">
            <input
              type="checkbox"
              checked={automount}
              onChange={(e) => setAutomount(e.target.checked)}
              className="mt-0.5 size-4 accent-accent"
            />
            <span>
              {t("volumes.automount")}
              <span className="block text-xs text-muted">{t("volumes.automountHint")}</span>
            </span>
          </label>
        )}
        <ErrorMessage error={attach.error} />
      </form>
    </Dialog>
  );
}

function DetachDialog({
  projectId,
  volume,
  onClose,
}: {
  projectId: string;
  volume: Volume;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const detach = useDetachVolume(projectId);
  const toast = useToast();
  return (
    <DangerDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("volumes.detachTitle", { name: volume.name, server: volume.server?.name ?? "" })}
      consequences={[
        t("volumes.detachUnmount"),
        t("volumes.detachKept"),
        t("volumes.detachBilling"),
      ]}
      actionLabel={t("volumes.detach")}
      busy={detach.isPending}
      onConfirm={() =>
        detach.mutate(
          { id: volume.id },
          {
            onSuccess: () => {
              toast({ tone: "ok", title: t("volumes.detaching", { name: volume.name }) });
              onClose();
            },
          },
        )
      }
    >
      <ErrorMessage error={detach.error} />
    </DangerDialog>
  );
}

function ResizeDialog({
  projectId,
  volume,
  onClose,
}: {
  projectId: string;
  volume: Volume;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const prices = useCatalogue(projectId, "prices").data;
  const resize = useResizeVolume(projectId);
  const toast = useToast();
  const [size, setSize] = useState(String(Math.min(volume.sizeGb + 10, VOLUME_MAX_GB)));
  const sizeGb = Number(size);
  const valid = Number.isInteger(sizeGb) && sizeGb > volume.sizeGb && sizeGb <= VOLUME_MAX_GB;
  const after =
    volume.format === "ext4"
      ? t("volumes.resizeExt4", { device: volume.linuxDevice })
      : volume.format === "xfs"
        ? t("volumes.resizeXfs")
        : t("volumes.resizeRaw");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    resize.mutate(
      { id: volume.id, body: { sizeGb } },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("volumes.resizing", { name: volume.name, size: sizeGb }) });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("volumes.resizeTitle", { name: volume.name })}
      description={t("volumes.resizeLead", { size: volume.sizeGb })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="resize-volume" busy={resize.isPending} disabled={!valid}>
            {t("volumes.resizeSubmit")}
          </Button>
        </>
      }
    >
      <form id="resize-volume" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("volumes.newSize")}
          type="number"
          inputMode="numeric"
          min={volume.sizeGb + 1}
          max={VOLUME_MAX_GB}
          step={1}
          value={size}
          onChange={(e) => setSize(e.target.value)}
          required
          hint={t("volumes.newSizeHint", { size: volume.sizeGb, max: VOLUME_MAX_GB })}
          error={fieldError(resize.error, "sizeGb", t)}
        />
        {valid && prices && (
          <p className="text-sm">
            {t("volumes.newPrice", {
              price: volumePrice(sizeGb, prices, i18n.language),
              old: volumePrice(volume.sizeGb, prices, i18n.language),
            })}
          </p>
        )}
        <Alert tone="neutral">
          <span className="break-words">{after}</span>
        </Alert>
        {!fieldError(resize.error, "sizeGb", t) && <ErrorMessage error={resize.error} />}
      </form>
    </Dialog>
  );
}

function EditDialog({
  projectId,
  volume,
  onClose,
}: {
  projectId: string;
  volume: Volume;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateVolume(projectId);
  const [name, setName] = useState(volume.name);
  const [labelsText, setLabelsText] = useState(formatLabels(volume.labels));
  const [badLine, setBadLine] = useState<number | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseLabels(labelsText);
    setBadLine(parsed.badLine);
    if (!parsed.labels) return;
    update.mutate(
      { id: volume.id, name: name.trim(), labels: parsed.labels },
      { onSuccess: onClose },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("volumes.editTitle", { name: volume.name })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="edit-volume" busy={update.isPending}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="edit-volume" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("volumes.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={64}
          spellCheck={false}
          autoCapitalize="off"
          hint={t("volumes.nameHint")}
          error={fieldError(update.error, "name", t)}
        />
        <TextArea
          label={t("volumes.labels")}
          value={labelsText}
          onChange={(e) => setLabelsText(e.target.value)}
          rows={4}
          spellCheck={false}
          placeholder="env=production"
          hint={t("volumes.labelsHint")}
          error={
            badLine !== null
              ? t("volumes.labelsInvalid", { line: badLine })
              : fieldError(update.error, "labels", t)
          }
        />
        <ErrorMessage error={update.error} />
      </form>
    </Dialog>
  );
}

type Open =
  | { kind: "detach" | "resize" | "edit" | "delete"; volume: Volume }
  | { kind: "attach"; volume: Volume; serverId: string | null }
  | { kind: "create"; serverId: string | null }
  | null;

/**
 * The row menu of a volume and the dialogs it opens, for the volumes page
 * and a server's volumes tab. `dialogs` must be rendered by the caller.
 */
export function useVolumeTools(projectId: string) {
  const { t } = useTranslation();
  const servers = useServers(projectId).data ?? [];
  const volumes = useVolumes(projectId).data ?? [];
  const protection = useVolumeProtection(projectId);
  const remove = useDeleteVolume(projectId);
  const toast = useToast();
  const [open, setOpen] = useState<Open>(null);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);
  const close = () => setOpen(null);

  const failed = (error: unknown) =>
    toast({
      tone: "danger",
      title:
        error instanceof ApiProblem && error.problem
          ? problemText(t, error.problem)
          : t("common.unexpected"),
    });

  const menu = (volume: Volume, busy: boolean): MenuItem[][] => {
    const attached = volume.server !== null;
    const creating = volume.status === "creating";
    const why = (fallback: string) =>
      readOnly ? readOnlyReason : busy ? t("servers.busy") : fallback;
    return [
      [
        {
          label: t("volumes.attach"),
          icon: <Link2 className="size-4" aria-hidden />,
          disabled: readOnly || busy || attached || creating,
          reason: why(
            creating
              ? t("volumes.why.creating")
              : t("volumes.why.attached", { server: volume.server?.name ?? "" }),
          ),
          onSelect: () => setOpen({ kind: "attach", volume, serverId: null }),
        },
        {
          label: t("volumes.detach"),
          icon: <Unlink className="size-4" aria-hidden />,
          disabled: readOnly || busy || !attached,
          reason: why(t("volumes.why.notAttached")),
          onSelect: () => setOpen({ kind: "detach", volume }),
        },
        {
          label: t("volumes.resize"),
          icon: <Expand className="size-4" aria-hidden />,
          disabled: readOnly || busy || creating || volume.sizeGb >= VOLUME_MAX_GB,
          reason: why(
            creating ? t("volumes.why.creating") : t("volumes.why.atMax", { max: VOLUME_MAX_GB }),
          ),
          onSelect: () => setOpen({ kind: "resize", volume }),
        },
        {
          label: t("volumes.edit"),
          icon: <Pencil className="size-4" aria-hidden />,
          disabled: readOnly,
          reason: readOnlyReason,
          onSelect: () => setOpen({ kind: "edit", volume }),
        },
      ],
      [
        {
          label: volume.protection.delete ? t("volumes.unprotect") : t("volumes.protect"),
          icon: volume.protection.delete ? (
            <LockOpen className="size-4" aria-hidden />
          ) : (
            <Lock className="size-4" aria-hidden />
          ),
          disabled: readOnly || busy,
          reason: why(""),
          onSelect: () =>
            protection.mutate(
              { id: volume.id, body: { delete: !volume.protection.delete } },
              { onError: failed },
            ),
        },
      ],
      [
        {
          label: t("volumes.delete"),
          icon: <Trash2 className="size-4" aria-hidden />,
          danger: true,
          disabled: readOnly || busy || volume.protection.delete || attached,
          reason: why(
            volume.protection.delete ? t("volumes.why.protected") : t("volumes.why.deleteAttached"),
          ),
          onSelect: () => setOpen({ kind: "delete", volume }),
        },
      ],
    ];
  };

  const deleting = open?.kind === "delete" ? open.volume : null;
  const dialogs: ReactNode = (
    <>
      {open?.kind === "create" && (
        <CreateDialog
          projectId={projectId}
          servers={servers}
          volumes={volumes}
          serverId={open.serverId}
          onClose={close}
        />
      )}
      {open?.kind === "attach" && (
        <AttachDialog
          projectId={projectId}
          volume={open.volume}
          servers={servers}
          volumes={volumes}
          serverId={open.serverId}
          onClose={close}
        />
      )}
      {open?.kind === "detach" && (
        <DetachDialog projectId={projectId} volume={open.volume} onClose={close} />
      )}
      {open?.kind === "resize" && (
        <ResizeDialog projectId={projectId} volume={open.volume} onClose={close} />
      )}
      {open?.kind === "edit" && (
        <EditDialog projectId={projectId} volume={open.volume} onClose={close} />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && close()}
        title={t("volumes.deleteTitle", { name: deleting?.name ?? "" })}
        consequences={[t("volumes.deleteData"), t("volumes.deleteBilling")]}
        confirmText={deleting?.name ?? ""}
        actionLabel={t("volumes.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("volumes.deleted", { name: deleting.name }) });
              close();
            },
            onError: failed,
          })
        }
      />
    </>
  );

  return {
    menu,
    dialogs,
    readOnly,
    readOnlyReason,
    tokenReadOnly,
    openCreate: (serverId: string | null) => setOpen({ kind: "create", serverId }),
    openAttach: (volume: Volume, serverId: string | null = null) =>
      setOpen({ kind: "attach", volume, serverId }),
  };
}

export function VolumesPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const volumes = useVolumes(projectId);
  const prices = useCatalogue(projectId, "prices").data;
  const running = useRunningOperations(projectId);
  const tools = useVolumeTools(projectId);
  const busy = (v: Volume) => progressOf(running.data, v.id) !== null;

  const createButton = (
    <Button
      onClick={() => tools.openCreate(null)}
      disabled={tools.readOnly}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("volumes.create")}
    </Button>
  );

  return (
    <>
      <PageHeader
        title={t("nav.volumes")}
        intro={t("volumes.intro")}
        help="volumes"
        actions={volumes.data?.length ? createButton : undefined}
      />
      {tools.tokenReadOnly && <Alert tone="warn">{tools.readOnlyReason}</Alert>}
      {volumes.error ? (
        <ErrorMessage error={volumes.error} />
      ) : (
        <DataTable<Volume>
          label={t("nav.volumes")}
          rows={volumes.data}
          rowKey={(v) => v.id}
          initialSort={{ key: "name", dir: "asc" }}
          progress={(v) => progressOf(running.data, v.id)}
          empty={
            <EmptyState
              icon={<HardDrive className="size-6" aria-hidden />}
              title={t("volumes.empty")}
              action={createButton}
              help="volumes"
            >
              {t("volumes.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("volumes.name"),
              primary: true,
              sort: (v) => v.name,
              cell: (v) => (
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium" title={v.name}>
                    {v.name}
                  </span>
                  <span className="truncate font-mono text-xs text-muted" title={v.linuxDevice}>
                    {v.linuxDevice.replace(/^\/dev\/disk\/by-id\//, "")}
                  </span>
                </span>
              ),
            },
            {
              key: "size",
              header: t("volumes.size"),
              align: "right",
              sort: (v) => v.sizeGb,
              cell: (v) => (
                <span className="whitespace-nowrap tabular-nums">
                  {t("volumes.sizeGb", { size: v.sizeGb })}
                </span>
              ),
            },
            {
              key: "format",
              header: t("volumes.fs"),
              sort: (v) => v.format ?? "",
              cell: (v) =>
                v.format ? <Badge>{v.format}</Badge> : <span className="text-muted">—</span>,
            },
            {
              key: "region",
              header: t("volumes.location"),
              sort: (v) => v.region,
              cell: (v) => <span className="text-muted">{v.region}</span>,
            },
            {
              key: "server",
              header: t("volumes.server"),
              sort: (v) => v.server?.name ?? "",
              cell: (v) =>
                v.server ? (
                  v.server.id ? (
                    <Link
                      to="/projects/$projectId/servers/$serverId"
                      params={{ projectId, serverId: v.server.id }}
                      search={{ tab: "volumes" }}
                      className="break-all text-accent hover:underline"
                    >
                      {v.server.name}
                    </Link>
                  ) : (
                    <span className="break-all">{v.server.name}</span>
                  )
                ) : (
                  <span className="whitespace-nowrap text-muted">{t("volumes.notAttached")}</span>
                ),
            },
            {
              key: "flags",
              header: t("volumes.flags"),
              cell: (v) => (
                <span className="flex flex-wrap gap-1">
                  {v.status === "creating" && <Badge tone="warn">{t("volumes.creating")}</Badge>}
                  {v.protection.delete && <Badge tone="ok">{t("volumes.protected")}</Badge>}
                  {v.status !== "creating" && !v.protection.delete && (
                    <span className="text-muted">—</span>
                  )}
                </span>
              ),
            },
            {
              key: "price",
              header: (
                <span className="inline-flex items-center gap-1">
                  {t("volumes.price")}
                  <FieldHint text={t("volumes.billing")} topic="volumes" />
                </span>
              ),
              align: "right",
              sort: (v) => v.sizeGb,
              cell: (v) => (
                <span className="whitespace-nowrap">
                  {volumePrice(v.sizeGb, prices, i18n.language)}
                </span>
              ),
            },
            {
              key: "created",
              header: t("volumes.createdAt"),
              hideOnPhone: true,
              sort: (v) => v.createdAt,
              cell: (v) => (
                <span className="text-muted">{relativeTime(v.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={(v) => tools.menu(v, busy(v))}
        />
      )}
      {tools.dialogs}
    </>
  );
}
